/**
 * A MIRAX slide, dropped as it sits on disk, becomes ONE image.
 *
 * 3DHISTECH writes a slide as `Slide.mrxs` (a small index) beside a folder
 * `Slide/` holding `Slidedat.ini`, `Index.dat` and the `Data0000.dat…` tiles:
 * tens of files and gigabytes that are nothing on their own. Until now a
 * person had to zip the two together; here the .mrxs and its folder, dropped
 * together (or the folder holding both), are grouped into one slide, which
 * the upload page packs into one tar (tar.ts) with the folder kept, so the
 * converter unpacks exactly what the scanner wrote.
 *
 * The data folder is known by its place: beside the .mrxs, named for it
 * (fileHandles.pathOf). A .mrxs dropped without its folder is refused as a
 * slide: sent alone it could only fail to convert. So is one whose folder came
 * without its Slidedat.ini, the map of the rest, which no reader opens a
 * MIRAX slide without (Windows' Chrome and Edge leave it out of a folder
 * walked by handles: fileHandles.missedByHandles).
 */

import { dirOf, pathOf } from './fileHandles';
import type { PickedSlide } from './dicomSlides';

const isMrxs = (f: File) => /\.mrxs$/i.test(f.name);
/** The file in a MIRAX data folder that says what the others hold. */
const SLIDEDAT = 'Slidedat.ini';

/** The MIRAX slides among `files`: each with its data folder's files, and their member names with the folder kept. */
export interface MiraxGroups {
  slides: PickedSlide[];
  /** A .mrxs whose data folder was not dropped with it: not a slide. */
  lone: File[];
  /** A .mrxs whose data folder came without its Slidedat.ini: not a slide either, and its folder's files go with it. */
  noSlidedat: File[];
  /** Every other file, as it was. */
  rest: File[];
}

export function miraxOf(files: readonly File[]): MiraxGroups {
  const taken = new Set<File>();
  const slides: PickedSlide[] = [];
  const lone: File[] = [];
  const noSlidedat: File[] = [];
  for (const mrxs of files.filter(isMrxs)) {
    const path = pathOf(mrxs);
    const dir = dirOf(path);
    const stem = mrxs.name.replace(/\.mrxs$/i, '');
    const dataDir = dir ? `${dir}/${stem}` : stem;
    // Its data folder's files, however deep, each named from the .mrxs's own folder: `Slide/Data0000.dat`.
    const data = files.filter((f) => {
      if (f === mrxs || taken.has(f) || f.name.startsWith('.')) return false;
      const d = dirOf(pathOf(f));
      return d === dataDir || d.startsWith(`${dataDir}/`);
    });
    if (!data.length) {
      lone.push(mrxs);
      continue;
    }
    const inOrder = [...data].sort((a, b) => (pathOf(a) < pathOf(b) ? -1 : pathOf(a) > pathOf(b) ? 1 : 0));
    const members = [mrxs, ...inOrder];
    members.forEach((f) => taken.add(f));
    // The exact name, in the folder itself: what OpenSlide opens, on a case-sensitive disk.
    if (!data.some((f) => f.name === SLIDEDAT && dirOf(pathOf(f)) === dataDir)) {
      noSlidedat.push(mrxs);
      continue;
    }
    slides.push({
      name: stem,
      holds: 'mrxs',
      files: members,
      paths: members.map((f) => pathOf(f).slice(dir ? dir.length + 1 : 0)),
    });
  }
  return { slides, lone, noSlidedat, rest: files.filter((f) => !taken.has(f) && !lone.includes(f)) };
}
